import { api } from "@/api/client";
import type { NetworkEnv } from "@/network/env";

export type WalletItem = {
  id: number;
  chain: string;
  networkEnv: string;
  address: string;
  label: string | null;
  purpose?: string | null;
  groupId?: string | null;
  hasRecovery?: boolean;
  createdAt?: string | null;
};

export type WalletsListResponse = {
  blockchainId: string;
  nanomUserId: string | null;
  networkEnv: string | null;
  items: WalletItem[];
};

export async function listWallets(
  token: string,
  networkEnv: NetworkEnv
): Promise<WalletsListResponse> {
  return api<WalletsListResponse>(
    `/wallets?networkEnv=${encodeURIComponent(networkEnv)}`,
    {},
    token
  );
}

export async function nextWalletLabel(token: string): Promise<string> {
  const out = await api<{ label: string }>("/wallets/next-label", {}, token);
  return out.label;
}

export async function createWallet(
  token: string,
  body: {
    pinDigits: string;
    pinLetters: string;
    networkEnv: NetworkEnv;
    label?: string;
    chain?: string;
  }
): Promise<unknown> {
  return api(
    "/wallets/create",
    {
      method: "POST",
      body: JSON.stringify({
        chain: body.chain || "both",
        label: body.label || null,
        pinDigits: body.pinDigits,
        pinLetters: body.pinLetters,
        forceNew: true,
        networkEnv: body.networkEnv,
      }),
    },
    token
  );
}

export async function importWallet(
  token: string,
  body: { pinDigits: string; pinLetters: string; groupId?: string }
): Promise<unknown> {
  return api(
    "/wallets/import",
    {
      method: "POST",
      body: JSON.stringify({
        pinDigits: body.pinDigits,
        pinLetters: body.pinLetters,
        groupId: body.groupId || null,
      }),
    },
    token
  );
}

export type DepositMe = {
  balanceUsdt?: string | number;
  depositAddress?: string | null;
};

export type NmcBalances = {
  ok?: boolean;
  address?: string;
  tokens?: Record<
    string,
    { balance?: string | number | null; scannerUrl?: string | null; error?: string }
  >;
  scanner?: { account?: string | null; nmc_account?: string | null; base?: string };
};

export async function fetchDepositMe(token: string): Promise<DepositMe> {
  return api<DepositMe>("/deposit/me", {}, token);
}

export async function ensureDepositAddress(token: string): Promise<{
  depositAddress?: string;
  network?: string;
  minDepositUsdt?: number | string;
}> {
  return api("/deposit/address", { method: "POST", body: "{}" }, token);
}

export async function fetchWithdrawTotp(token: string): Promise<{ enabled?: boolean; required?: boolean }> {
  return api("/security/withdraw-totp", {}, token);
}

export async function fetchWithdrawFeeQuote(
  token: string,
  amountUsdt: string
): Promise<{ feeUsdt?: string; totalUsdt?: string; netUsdt?: string; [k: string]: unknown }> {
  return api(`/withdraw/fee-quote?amountUsdt=${encodeURIComponent(amountUsdt)}&rail=external`, {}, token);
}

export async function createWithdraw(
  token: string,
  body: {
    toAddress: string;
    amountUsdt: string;
    fromAddress?: string;
    totpCode?: string;
    idempotencyKey?: string;
  }
): Promise<unknown> {
  return api("/withdraw/create", { method: "POST", body: JSON.stringify(body) }, token);
}

export async function fetchIrrBalance(token: string): Promise<{ balanceIrr?: number }> {
  return api("/irr/balance", {}, token);
}

export async function fetchNmcBalances(token: string, address: string): Promise<NmcBalances> {
  return api<NmcBalances>(`/nmc/balances/${encodeURIComponent(address)}`, {}, token);
}

export function scannerAddressUrl(address: string, base = "https://ai.astromehr.com/nmc/scanner"): string {
  const a = address.trim();
  const root = base.replace(/\/+$/, "");
  return `${root}/#/address/${a}`;
}
